import { useCallback, useRef, useState } from "react";
import { AmountField } from "../components/AmountField";
import { AsyncPanel } from "../components/AsyncPanel";
import { Button } from "../components/Button";
import { CheckboxField, SelectField, TextAreaField, TextField } from "../components/Field";
import { Skeleton } from "../components/Skeleton";
import { VisuallyHidden } from "../components/VisuallyHidden";
import { ApiError } from "../api/client";
import { fetchSubmissionTerms, submitSellerDeal } from "../api/submission";
import { useApiResource } from "../hooks/useApiResource";
import type { Resource } from "../hooks/useApiResource";
import { useSession } from "../session/useSession";
import { labelForFieldName } from "../lib/deal-fields";
import { messageForSubmissionError } from "../lib/submission-messages";
import {
  DEAL_TYPE_OPTIONS,
  EMPTY_SUBMISSION,
  prepareSubmission,
} from "../lib/submission-form";
import styles from "./SubmitDeal.module.css";
import type { FormEvent } from "react";
import type { SubmitFieldErrors, SubmitFormValues } from "../lib/submission-form";
import type { DealSubmission, SubmissionTerms, SubmittedDeal } from "../api/types";

/**
 * Filing a deal for review, from the signed-in dashboard (canon APP-009, decision `D-008`).
 *
 * The form used to live on `dealbid-web` and to carry the founder's name, email address and
 * invite code, because an anonymous endpoint had no other way to know who was submitting.
 * `D-008` puts submission behind the session, so all three are gone: the founder is decided
 * by the cookie, and canon § 5's signup source was settled once, at sign-in. There is no box
 * here a founder could fill in to change who Dealbid thinks they are.
 *
 * Three things are load-bearing.
 *
 * **Submitting is not listing.** Canon §§ 2 and 3 gate every deal on a manual review of the
 * product, the founder's connection to it, the links and the advertised value, so what
 * review actually checks is stated *before* the form rather than discovered afterwards, and
 * the confirmation says "in review" rather than anything that sounds like acceptance.
 *
 * **The sentence a founder accepts is core's sentence.** Core stamps the version of the
 * wording it holds onto the deal (CORE-016), so the form displays what
 * `GET /v1/submission-terms` returns and the submit control stays disabled until it has it.
 * A constant in this repo could be reworded without that version moving, which would leave a
 * founder recorded as having accepted a sentence nobody showed them.
 *
 * **Nothing here grants standing.** Status, slug, Founding position, verification and the
 * signup source are all core's to decide. This form sends what a founder can actually
 * describe, and displays what core decided.
 *
 * The form lives inside the deals panel rather than on a route of its own: a founder watches
 * their deal move through review on that panel, and the deal they just filed belongs at the
 * top of the same list rather than behind a navigation step.
 */

export interface SubmitDealProps {
  /** Test seams. The shipped app always reads dealbid-core. */
  loadTerms?: (() => Promise<SubmissionTerms>) | undefined;
  submitDeal?: ((submission: DealSubmission) => Promise<SubmittedDeal>) | undefined;
  /** Called with core's own answer once a submission is accepted. */
  onSubmitted: (created: SubmittedDeal) => void;
  /** Leaves the form. Absent for a founder who has no deals — there is nothing to go back to. */
  onCancel?: (() => void) | undefined;
}

export function SubmitDeal({
  loadTerms = fetchSubmissionTerms,
  submitDeal = submitSellerDeal,
  onSubmitted,
  onCancel,
}: SubmitDealProps) {
  const heading = useRef<HTMLHeadingElement>(null);

  const termsLoader = useCallback(() => loadTerms(), [loadTerms]);
  const terms = useApiResource(termsLoader);

  // Focus moves to the form's own heading when it opens, so a keyboard or screen-reader user
  // is taken to what just appeared rather than left at the button that opened it. A founder
  // with no deals meets the form already on the page and is not moved: nothing happened for
  // the focus to follow.
  const focusHeading = useCallback(
    (node: HTMLHeadingElement | null) => {
      heading.current = node;
      if (onCancel !== undefined) node?.focus();
    },
    [onCancel],
  );

  return (
    <section className={styles.submit} aria-labelledby="submit-deal-heading">
      <h3 className={styles.heading} id="submit-deal-heading" tabIndex={-1} ref={focusHeading}>
        Submit a deal for review
      </h3>

      <Guidance />

      {/*
        * Only the consent control waits on the terms read, never the whole form. The wording
        * is required before a submission can be *sent* — `acceptedTerms` is a claim that a
        * specific sentence was shown — but it is not required to describe a deal, and a
        * founder who has typed nine fields should not lose them because one read failed.
        */}
      <SubmitForm
        terms={terms}
        submitDeal={submitDeal}
        onSubmitted={onSubmitted}
        {...(onCancel === undefined ? {} : { onCancel })}
      />
    </section>
  );
}

/**
 * What Dealbid lists and what review checks, before the boxes rather than after them.
 *
 * Canon § 2 sets the eligibility bar and § 14 makes ranking integrity a separate gate from
 * bidding: a large bid cannot make an ineligible deal eligible. Saying so here is what stops
 * a founder reading the auction rules further down the desk and concluding that submitting
 * and winning are the same act.
 */
function Guidance() {
  return (
    <div className={styles.guidance}>
      <p className={styles.lede}>
        Every submission is reviewed by a person before it reaches the board. Nothing is
        public until then, and no bid can make an ineligible deal eligible — eligibility is
        decided first, and separately.
      </p>
      <div className={styles.guides}>
        <div className={styles.guide}>
          <h4 className={styles.guideTitle}>What counts as a deal</h4>
          <p>
            Ordinarily a real discount of 30% or more, or equivalent value: a lifetime deal, a
            launch or founder offer, a meaningful annual discount, or materially enhanced
            credits or usage. Less than that can still be approved when the value is clearly
            compelling. A deal does not have to be exclusive to Dealbid, and a nominal or
            misleading discount is not eligible.
          </p>
        </div>
        <div className={styles.guide}>
          <h4 className={styles.guideTitle}>What review checks</h4>
          <ul className={styles.checks} role="list">
            <li>the product exists and works well enough to evaluate</li>
            <li>you are genuinely connected to it</li>
            <li>the product and deal links both work</li>
            <li>the advertised price or value is genuine</li>
            <li>important limitations are disclosed</li>
            <li>nothing is deceptive, abusive or against policy</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

type SubmitStatus = "idle" | "submitting" | "error";

/** Core's dotted field name to the label on the control it belongs to. */
function labelForIssue(field: string): string {
  return labelForFieldName(field.startsWith("deal.") ? field.slice("deal.".length) : field);
}

function SubmitForm({
  terms,
  submitDeal,
  onSubmitted,
  onCancel,
}: {
  terms: Resource<SubmissionTerms>;
  submitDeal: NonNullable<SubmitDealProps["submitDeal"]>;
  onSubmitted: SubmitDealProps["onSubmitted"];
  onCancel?: (() => void) | undefined;
}) {
  const { retry } = useSession();
  const termsText = terms.state.status === "ready" ? terms.state.data.text : null;
  const [form, setForm] = useState<SubmitFormValues>(EMPTY_SUBMISSION);
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [fieldErrors, setFieldErrors] = useState<SubmitFieldErrors>({});
  const [otherIssues, setOtherIssues] = useState<string[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const inFlight = useRef(false);

  const set = useCallback(
    <K extends keyof SubmitFormValues>(key: K, value: SubmitFormValues[K]) => {
      setForm((previous) => ({ ...previous, [key]: value }));
      // Editing is the founder starting again: last time's verdict no longer describes what
      // is in the boxes, and leaving it on screen would make a stale error look current.
      setStatus("idle");
      setFieldErrors({});
      setOtherIssues([]);
      setFailure(null);
    },
    [],
  );

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (inFlight.current) return;

    const prepared = prepareSubmission(form, termsText);
    if (!prepared.ok) {
      setStatus("error");
      setFieldErrors(prepared.fieldErrors);
      setFailure(null);
      return;
    }

    inFlight.current = true;
    setStatus("submitting");
    setFieldErrors({});
    setOtherIssues([]);
    setFailure(null);

    void submitDeal(prepared.body)
      .then((created) => {
        onSubmitted(created);
      })
      .catch((caught: unknown) => {
        const error =
          caught instanceof ApiError
            ? caught
            : new ApiError("Dealbid couldn't submit this deal.", { status: null });

        // Core names the offending inputs, so each message is shown against the box it is
        // about. A field this app draws no control for still has its message read out at the
        // foot of the form rather than being dropped.
        const named: SubmitFieldErrors = {};
        const unnamed: string[] = [];
        for (const issue of error.fields) {
          if (issue.field !== "" && Object.hasOwn(CONTROLS, issue.field)) {
            named[issue.field] = issue.message;
          } else {
            unnamed.push(
              issue.field === ""
                ? issue.message
                : `${labelForIssue(issue.field)}: ${issue.message}`,
            );
          }
        }

        setFieldErrors(named);
        setOtherIssues(unnamed);
        setFailure(messageForSubmissionError(error));
        setStatus("error");
      })
      .finally(() => {
        inFlight.current = false;
      });
  }

  const busy = status === "submitting";
  const error = (field: string) =>
    fieldErrors[field] === undefined ? {} : { error: fieldErrors[field] };

  return (
    <form className={styles.form} onSubmit={submit} noValidate>
      <div className={styles.fields}>
        <TextField
          label="Product name"
          value={form.productName}
          onChange={(value) => {
            set("productName", value);
          }}
          disabled={busy}
          {...error("deal.productName")}
        />

        <TextField
          label="Product website"
          type="url"
          value={form.productUrl}
          onChange={(value) => {
            set("productUrl", value);
          }}
          disabled={busy}
          hint="The site a reviewer will check the product on."
          {...error("deal.productUrl")}
        />

        <TextAreaField
          label="Short description"
          value={form.shortDescription}
          onChange={(value) => {
            set("shortDescription", value);
          }}
          disabled={busy}
          hint="What the product does, in a sentence a buyer can scan. This is what appears on the board."
          {...error("deal.shortDescription")}
        />

        <TextField
          label="Deal link"
          type="url"
          value={form.dealUrl}
          onChange={(value) => {
            set("dealUrl", value);
          }}
          disabled={busy}
          hint="Where a buyer lands when they open the deal. It must work without a Dealbid account."
          {...error("deal.dealUrl")}
        />

        <SelectField
          label="Deal type"
          value={form.dealType}
          onChange={(value) => {
            set("dealType", value);
          }}
          options={DEAL_TYPE_OPTIONS}
          placeholder="Choose a deal type"
          disabled={busy}
          {...error("deal.dealType")}
        />

        {/* A before and an after are one fact read together, so they sit side by side. */}
        <div className={styles.pair}>
          <AmountField
            label="Usual price"
            value={form.originalPrice}
            onChange={(value) => {
              set("originalPrice", value);
            }}
            disabled={busy}
            hint="Leave empty if this deal has no before price."
            {...error("deal.originalPriceCents")}
          />
          <AmountField
            label="Deal price"
            value={form.dealPrice}
            onChange={(value) => {
              set("dealPrice", value);
            }}
            disabled={busy}
            hint="What a buyer pays through this deal."
            {...error("deal.dealPriceCents")}
          />
        </div>

        <TextAreaField
          label="What the deal is worth"
          optional
          value={form.valueNotes}
          onChange={(value) => {
            set("valueNotes", value);
          }}
          disabled={busy}
          hint="Required instead of prices when the value isn't a before and after figure — “triple credits for the first year”, for example. Dealbid has to be able to judge whether the discount is real."
          {...error("deal.valueNotes")}
        />

        <TextField
          label="Coupon code"
          optional
          value={form.couponCode}
          onChange={(value) => {
            set("couponCode", value);
          }}
          disabled={busy}
          hint="Only if a buyer has to enter one."
          {...error("deal.couponCode")}
        />

        <TextAreaField
          label="Limitations"
          optional
          value={form.limitations}
          onChange={(value) => {
            set("limitations", value);
          }}
          disabled={busy}
          hint="New accounts only, seat caps, an end date — anything a buyer would be annoyed to discover at checkout."
          {...error("deal.limitations")}
        />

        <CheckboxField
          label="This offer is exclusive to Dealbid"
          checked={form.isExclusive}
          onChange={(checked) => {
            set("isExclusive", checked);
          }}
          disabled={busy}
          hint="Only tick this while the offer and its terms are not available elsewhere. Reviewers check it before the Exclusive badge is applied."
          {...error("deal.isExclusive")}
        />

        <TextField
          label="Logo image URL"
          optional
          type="url"
          value={form.logoUrl}
          onChange={(value) => {
            set("logoUrl", value);
          }}
          disabled={busy}
          hint="Shown beside the deal on the board. A square image reads best."
          {...error("deal.logoUrl")}
        />
      </div>

      {/*
        * Core's own sentence, shown verbatim as the label of the consent box. The version
        * beside it is what core stamps onto the deal, so the wording on screen and the
        * wording the acceptance names are provably one sentence.
        */}
      <div className={styles.terms}>
        <AsyncPanel
          state={terms.state}
          reload={terms.reload}
          onSessionEnded={retry}
          loadingLabel="Loading the terms you are agreeing to"
          skeleton={<Skeleton width="100%" height="3rem" />}
          errorTitle="Dealbid couldn't load the terms to agree to"
        >
          {(data) => (
            <>
              <CheckboxField
                label={data.text}
                checked={form.acceptedTerms}
                onChange={(checked) => {
                  set("acceptedTerms", checked);
                }}
                disabled={busy}
                {...error("acceptedTerms")}
              />
              <p className={styles.version}>
                Dealbid records this wording, version{" "}
                <span className="figure">{data.version}</span>, with your submission.
              </p>
            </>
          )}
        </AsyncPanel>
      </div>

      <div className={styles.actions}>
        {/* Disabled until the wording is on screen: sending `acceptedTerms: true` without
            it would record an agreement to a sentence nobody displayed. */}
        <Button type="submit" disabled={busy || termsText === null}>
          {busy ? "Submitting…" : "Submit for review"}
        </Button>
        {onCancel === undefined ? null : (
          <Button variant="quiet" onClick={onCancel} disabled={busy}>
            <span aria-hidden="true">Cancel</span>
            <VisuallyHidden>Cancel this submission</VisuallyHidden>
          </Button>
        )}
      </div>

      {failure === null ? null : (
        <div className={styles.failure} role="alert">
          <p>
            <VisuallyHidden>Error: </VisuallyHidden>
            {failure}
          </p>
          {otherIssues.length === 0 ? null : (
            <ul className={styles.issues}>
              {otherIssues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </form>
  );
}

/**
 * The field names this form draws a control for.
 *
 * It exists so a refusal naming a field this bundle does not render is read out at the foot
 * of the form instead of being dropped: core is the newer half of the contract, and a
 * message with nowhere to go must still reach the founder.
 */
const CONTROLS: Record<string, true> = {
  "deal.productName": true,
  "deal.productUrl": true,
  "deal.logoUrl": true,
  "deal.shortDescription": true,
  "deal.dealUrl": true,
  "deal.dealType": true,
  "deal.originalPriceCents": true,
  "deal.dealPriceCents": true,
  "deal.valueNotes": true,
  "deal.couponCode": true,
  "deal.limitations": true,
  "deal.isExclusive": true,
  acceptedTerms: true,
};
